/**
 * The task going to Recents (founder 09-01: "a beautiful realistic feel that
 * this task is going there"). Once Bash has flown home, the receipt row on the
 * pill detaches as a small card, travels to its own row at the top of the
 * Recents list (or to where that row will appear), shrinks into it and fades;
 * the row glows for a beat. Pure DOM, transform + opacity only, and a no-op
 * where it cannot run (no Web Animations, reduced motion, no target).
 */
const FLIGHT_MS = 720;
const LANDED_MS = 1100;

const escapeId = (id: string) =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(id)
    : id.replace(/["\\]/g, "\\$&");

/** Where the task lands: its own row, else the newest row, else the panel. */
export function recentsTarget(taskId: string): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>(
      `.bh-row[data-task-id="${escapeId(taskId)}"]`
    ) ??
    document.querySelector<HTMLElement>(".bh-rows .bh-row") ??
    document.querySelector<HTMLElement>(".bh-panel")
  );
}

/** The scroll container the Recents rows live in — the panel that overflows. */
export function recentsScroller(): HTMLElement | null {
  return (
    document
      .querySelector<HTMLElement>(".bh-rows")
      ?.closest<HTMLElement>(".bh-panel") ??
    document.querySelector<HTMLElement>(".bh-panel")
  );
}

/**
 * Bring the top of Recents into view before a task flies home (founder 09-03).
 * A new task lands at the "now" slot at the very top; when the list is scrolled
 * down that slot sits above the viewport, so the flight ends where it cannot be
 * seen ("it disappears"). Smooth-scroll the panel to the top so the landing row
 * is on screen by the time the flight measures it. A no-op when already there.
 */
export function scrollRecentsToTop(): void {
  const el = recentsScroller();
  if (el && el.scrollTop > 0 && typeof el.scrollTo === "function")
    el.scrollTo({ top: 0, behavior: "smooth" });
}

export function flyTaskToRecents(
  from: HTMLElement | null,
  taskId: string
): Promise<void> {
  if (!from || typeof from.animate !== "function") return Promise.resolve();
  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
    return Promise.resolve();
  const target = recentsTarget(taskId);
  if (!target) return Promise.resolve();

  const a = from.getBoundingClientRect();
  const b = target.getBoundingClientRect();
  const ghost = from.cloneNode(true) as HTMLElement;
  ghost.classList.add("bh-ghost");
  ghost.removeAttribute("aria-label");
  ghost.setAttribute("aria-hidden", "true");
  // Measured at runtime: the card starts exactly where the row was.
  ghost.style.left = `${a.left}px`;
  ghost.style.top = `${a.top}px`;
  ghost.style.width = `${a.width}px`;
  ghost.style.height = `${a.height}px`;
  ghost.style.margin = "0";
  document.body.appendChild(ghost);

  const sx = a.width > 0 ? Math.max(0.2, b.width / a.width) : 1;
  const sy = a.height > 0 ? Math.max(0.2, Math.min(1, b.height / a.height)) : 1;
  const anim = ghost.animate(
    [
      { transform: "translate(0px, 0px) scale(1, 1)", opacity: 1, offset: 0 },
      { opacity: 1, offset: 0.82 },
      {
        transform: `translate(${b.left - a.left}px, ${b.top - a.top}px) scale(${sx}, ${sy})`,
        opacity: 0,
        offset: 1,
      },
    ],
    {
      duration: FLIGHT_MS,
      easing: "cubic-bezier(.2, .8, .2, 1)",
      fill: "forwards",
    }
  );
  return anim.finished
    .catch(() => undefined)
    .then(() => {
      ghost.remove();
      const row = recentsTarget(taskId);
      if (row && row.classList.contains("bh-row")) {
        row.classList.add("bh-row--landed");
        window.setTimeout(
          () => row.classList.remove("bh-row--landed"),
          LANDED_MS
        );
      }
    });
}
